import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Button, Col, Divider, Modal, Row, Typography } from "antd";

import { useUserContext } from "../../contexts/UserContext";
import {
  ListRow,
  SectionRow,
  StyledModal,
  Total,
  Error,
} from "./styledComponents";
import DeclarationStatusSelect from "../DeclarationStatusSelect";
import { IUser } from "../../../../../types";
import WarnLeaveWithoutSaving from "../../../../common/WarnLeaveWithoutSaving";
import { WARNING_MESSAGE } from "../../../../common/WarnLeaveWithoutSaving/constants";
import { getGraphQLErrorMessage } from "../../../../../utils/error";
import { showGraphQlErrorNotification } from "../../../../../utils/notifications";
import DeclarationApprovalResetConfirmation from "../DeclarationApprovalResetConfirmation";

const { Text } = Typography;

export interface DeclarationStatusChangeModalProps<T extends string> {
  title: string;
  year: number;
  user?: IUser;
  subtitle?: string;
  status: T;
  statusOptions: T[];
  statusMapping: Record<T, { label: string }>;
  onSave: (status: T, options?: { resetApproval?: boolean }) => Promise<void>;
  onClose: () => void;
  children?: ReactNode;
  /*
   * Opt-in, because this component is shared with the EÜR subform status modals
   * (car, office, travel, business assets). Subforms have no customer approval,
   * so they never pass this and can never reach the reset confirmation.
   */
  canResetCustomerApproval?: boolean;
}

const DeclarationStatusChangeModal = <T extends string>({
  title,
  year,
  user: userFromProps,
  subtitle,
  status: initialStatus,
  statusOptions,
  statusMapping,
  children,
  onSave,
  onClose,
  canResetCustomerApproval,
}: DeclarationStatusChangeModalProps<T>) => {
  const userFromContext = useUserContext();
  const user = userFromProps || userFromContext;
  const [status, setStatus] = useState<T>(initialStatus);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [
    isApprovalResetConfirmationVisible,
    setIsApprovalResetConfirmationVisible,
  ] = useState(false);

  useEffect(() => {
    setStatus(initialStatus);
  }, [initialStatus]);

  const isDirty = useMemo(
    () => initialStatus !== status,
    [initialStatus, status]
  );

  const save = useCallback(
    async (options?: { resetApproval?: boolean }) => {
      try {
        /* Clear a stale inline message before a different failure notifies. */
        setErrorMessage("");
        setIsSaving(true);
        await onSave(status, options);
        onClose();
      } catch (error) {
        const errorMessage = getGraphQLErrorMessage(error);
        if (
          errorMessage &&
          errorMessage.message === "APPROVAL_RESET_REQUIRED" &&
          canResetCustomerApproval
        ) {
          /*
           * Not an error the operator has to fix: the backend is asking whether
           * the customer's approval may be reset along with the status change.
           */
          setIsSaving(false);
          setIsApprovalResetConfirmationVisible(true);
          return;
        }
        if (
          errorMessage &&
          errorMessage.message === "DOCUMENTS_REQUIRED_FOR_STATUS_UPDATE"
        ) {
          setErrorMessage(
            "Ein Dokument muss hochgeladen sein, um diesen Status setzen zu können"
          );
        } else {
          /*
           * Everything else used to reach the browser console only, so the modal
           * just stopped saving and said nothing. That is worst on the retry
           * after the reset confirmation, where the operator explicitly said yes
           * and would otherwise be left to guess whether anything happened.
           */
          showGraphQlErrorNotification(
            "Status konnte nicht geändert werden:",
            error
          );
        }

        setIsSaving(false);
      }
    },
    [canResetCustomerApproval, onClose, onSave, status]
  );

  const handleSaveAndClose = useCallback(() => save(), [save]);

  const handleApprovalResetConfirm = useCallback(async () => {
    setIsApprovalResetConfirmationVisible(false);
    await save({ resetApproval: true });
  }, [save]);

  /* Cancelling the reset cancels the status change too: neither is applied. */
  const handleApprovalResetCancel = useCallback(() => {
    setIsApprovalResetConfirmationVisible(false);
  }, []);

  const handleClose = useCallback(() => {
    if (isDirty) {
      Modal.confirm({
        title: WARNING_MESSAGE,
        onOk: onClose,
        cancelText: "Abbrechen",
      });
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  if (!user) {
    return null;
  }

  return (
    <>
      <WarnLeaveWithoutSaving warn={isDirty} />
      <StyledModal
        visible
        title={
          <>
            <Text strong>{`${title} ${year}`}</Text>
            <Text> Statusübersicht</Text>
          </>
        }
        maskClosable={false}
        onCancel={handleClose}
        width={800}
        footer={
          <>
            <Button
              data-test="saveAnlagevermogenStatusButton"
              type="primary"
              onClick={handleSaveAndClose}
              loading={isSaving}
            >
              Speichern und schließen
            </Button>
          </>
        }
      >
        <SectionRow>
          <Col>
            <Text strong>
              {user.firstName} {user.lastName}
            </Text>
            <div>{user.email}</div>
            <div>{subtitle}</div>
          </Col>
        </SectionRow>

        <Divider className="m-0" />

        {children}

        <Divider className="m-0" />

        <SectionRow>
          <Row>
            <DeclarationStatusSelect
              value={status}
              options={statusOptions}
              mapping={statusMapping}
              onChange={setStatus}
              disabled={isSaving}
              error={!!errorMessage}
            />
          </Row>
          <Row>{!!errorMessage && <Error>{errorMessage}</Error>}</Row>
        </SectionRow>
      </StyledModal>

      {isApprovalResetConfirmationVisible && (
        <DeclarationApprovalResetConfirmation
          loading={isSaving}
          onConfirm={handleApprovalResetConfirm}
          onCancel={handleApprovalResetCancel}
        />
      )}
    </>
  );
};

DeclarationStatusChangeModal.ListRow = ListRow;
DeclarationStatusChangeModal.Total = Total;

export default DeclarationStatusChangeModal;
